import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { isFunction } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';
import {
  BannerActionsEnum,
  BannerTypesEnum,
  IBannerTrigger,
} from './banner.data';
import { BannerService } from './banner.service';
import { IconSizesEnum, IIcon } from '../../components/icon/icon.component';
import { ISticky } from '../../directives/sticky/sticky.directive';

@Component({
  selector: 'i2g-banner',
  templateUrl: 'banner.component.html',
  styleUrls: ['banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerComponent implements OnInit, OnDestroy {
  @ViewChild('globalBanner', { static: true }) globalBanner: ElementRef;
  @ViewChild('globalBannerMessage', { static: false })
  globalBannerMessage: ElementRef;

  private _id: string;
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _persistent: boolean;
  private _onDestroy: (bannerId: string, component: BannerComponent) => void;
  private _onDismiss: (bannerId: string, component: BannerComponent) => void;
  private _onAction: (bannerId: string, component: BannerComponent) => void;

  isVisible: boolean;
  actionIcon: IIcon;
  dismissible: boolean;
  type: BannerTypesEnum;
  message: string;
  containsHtml: boolean;
  trackingObject: ITrackingObject;
  trackingAction: ITrackingAction;
  trackingScreenName: string;
  trackingAdditionalProperties: any;
  stickyConfig: ISticky;
  e2eId: string;

  constructor(
    private _bannerService: BannerService,
    private _trackingService: I2gNgTrackingService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this._bannerService.bannerTriggers$
      .pipe(takeUntil(this._destroyed))
      .subscribe((bannerTrigger: IBannerTrigger) => {
        if (bannerTrigger) {
          switch (bannerTrigger.action) {
            case BannerActionsEnum.hide:
              this._id = null;

              this.stickyConfig = null;
              this.isVisible = false;
              break;

            case BannerActionsEnum.show:
              const { banner, type } = bannerTrigger;
              const trackingScreenName = banner.trackingScreenName;

              this._id = banner.id;
              this._persistent = !!banner.persistent;
              this._onDestroy = banner.onDestroy;
              this._onDismiss = banner.onDismiss;
              this._onAction = banner.onAction;

              this.type = type;
              this.isVisible = true;
              this.message = banner.message;
              this.containsHtml = banner.containsHtml;
              this.dismissible = banner.dismissible;
              this.trackingObject = banner.trackingObject;
              this.trackingAction = banner.trackingAction;
              this.e2eId = banner.e2eId;
              this.trackingAdditionalProperties = {
                target: banner.bannerDestination,
              };

              if (trackingScreenName) {
                if (!this.trackingAction) {
                  this.trackingAction = this._trackingActionBuilder.buildTapped(
                    trackingScreenName
                  );
                }

                if (this.trackingObject) {
                  this._trackingService.trackEvent(
                    this.trackingObject,
                    this._trackingActionBuilder.buildPresented(
                      trackingScreenName
                    )
                  );
                }
              }

              if (this._onAction) {
                this.actionIcon = {
                  name: 'chevron-right',
                  size: IconSizesEnum.medium,
                };
              } else if (this.dismissible) {
                this.actionIcon = {
                  name: 'close',
                  size: IconSizesEnum.medium,
                };
              } else {
                this.actionIcon = null;
              }

              setTimeout(() => {
                if (banner.isSticky) {
                  this.stickyConfig = { scrollEl: '.app-page-wrapper' };

                  this._changeDetectorRef.markForCheck();
                }

                if (isFunction(banner.onShow)) {
                  banner.onShow(this._id, this);
                }
              }, 100);
              break;

            default:
              break;
          }
        }

        this._changeDetectorRef.markForCheck();
      });
  }

  onBannerAction(evt: Event, stopPropagation: boolean) {
    if (stopPropagation) {
      evt.stopPropagation();
    }

    if (isFunction(this._onAction)) {
      this._onAction(this._id, this);
    }

    if (this.dismissible) {
      this._bannerService.hideBanner(this.type);

      if (isFunction(this._onDismiss)) {
        this._onDismiss(this._id, this);
      }
    }
  }

  ngOnDestroy() {
    if (this.isVisible && !this._persistent) {
      this._bannerService.hideBanner(this.type);
    }

    if (isFunction(this._onDestroy)) {
      this._onDestroy(this._id, this);
    }

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
